import React, { useState, useRef } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";
import axios from "../../../Axios";
import debounce from "lodash.debounce";
import { generateReactSelectOptions } from "../../../Utility";
import { customStyles } from "../Forms/Dropdown/DropdownCustomStyle";
import classes from "./AutoComplete.module.css";

const AutoComplete = ({
  remoteUrl,
  onSelect,
  defaultValue,
  dataKey,
  valueKey = "name",
  multiSelect = false,
  trimmed,
  required,
  placeholder = "Select",
  clearSearchText = false,
  invalid,
  hideAsterisk,
  className,
  value,
  onChange,
  creatable = true,
  onChangeEventUsed = false,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVal, setSelectedVal] = useState(
    () => defaultValue || (multiSelect ? [] : null)
  );
  const searchedTextRef = useRef("");

  const loadData = async (searchText, callback) => {
    if (!remoteUrl) return;
    setIsLoading(true);
    try {
      const url = remoteUrl.replace("{0}", searchText);
      const res = await axios.get(url);
      callback(
        generateReactSelectOptions(
          dataKey ? res.data[dataKey] : res.data,
          valueKey,
          trimmed
        )
      );
    } catch (err) {
      console.error(err);
      callback([]);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedLoadOptions = debounce(loadData, 500);

  const onInputChange = (val, actionMeta) => {
    if (actionMeta.action === "input-change") {
      searchedTextRef.current = val;
    }
    if (actionMeta.action === "input-blur") {
      if (multiSelect) multiSelectList();
      else singleSelect();
    }
  };

  const multiSelectList = () => {
    if (searchedTextRef.current?.trim()) {
      const updatedValues = [
        ...(selectedVal || []),
        { label: searchedTextRef.current, value: searchedTextRef.current },
      ];
      setSelectedVal(updatedValues);
      onSelect(updatedValues);
      searchedTextRef.current = "";
    }
  };

  const singleSelect = () => {
    if (searchedTextRef.current?.trim()) {
      const selectedObject = {
        label: searchedTextRef.current,
        value: searchedTextRef.current,
      };
      setSelectedVal(clearSearchText ? "" : selectedObject);
      onSelect(selectedObject);
      searchedTextRef.current = "";
    }
  };

  const onSelectionChange = (val) => {
    searchedTextRef.current = "";
    if (multiSelect) {
      setSelectedVal(val);
      onSelect(val);
    } else {
      setSelectedVal(clearSearchText ? "" : val);
      onSelect(val);
    }
  };

  const updateSelection = (val, action) => {
    onInputChange(val, action);
    if (onChangeEventUsed) onChange(val);
  };

  return (
    <div className={`${classes.container} ${className}`}>
      {required && !hideAsterisk && (
        <span className={classes["asterisk"]}>*</span>
      )}
      <div className={`${classes.content} ${invalid ? classes.invalid : ""}`}>
        {creatable ? (
          <AsyncCreatableSelect
            loadOptions={debouncedLoadOptions}
            isLoading={isLoading}
            onChange={onSelectionChange}
            styles={customStyles}
            isMulti={multiSelect}
            value={selectedVal}
            onInputChange={updateSelection}
            placeholder={placeholder}
            inputValue={value}
            isDisabled={disabled}
            onCreateOption={(inputValue) => {
              const newOption = { label: inputValue, value: inputValue };
              if (multiSelect) {
                const updated = [...(selectedVal || []), newOption];
                setSelectedVal(updated);
                onSelect(updated);
              } else {
                setSelectedVal(newOption);
                onSelect(newOption);
              }
            }}
          />
        ) : (
          <AsyncSelect
            loadOptions={debouncedLoadOptions}
            isLoading={isLoading}
            onChange={onSelectionChange}
            styles={customStyles}
            isMulti={multiSelect}
            value={selectedVal}
            onInputChange={updateSelection}
            placeholder={placeholder}
            inputValue={value}
            isDisabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

export default AutoComplete;
